import React, { ReactElement, useMemo } from 'react';
import { Text } from 'rsuite';
import { convertNumberToCurrency } from '../../../common/utils';
import { TaxableReceipt } from 'src/types/tax';
import { TaxConfigurationInformation } from 'src/types/configuration';
import LabelWithInfoIcon from '../../common/LabelWithInfoIcon';

interface TaxableReceiptsBreakdownProps {
  configuration?: TaxConfigurationInformation;
  dark?: boolean;
  receipt: TaxableReceipt;
}

export const TaxableReceiptsBreakdown = ({
  configuration,
  dark,
  receipt: { accruedInterest, latePenalties, total, totDue, credit },
}: TaxableReceiptsBreakdownProps): ReactElement => {
  const rowStyle = {
    background: dark ? '#f5f5f5' : '#fff',
  };

  const dueLabel = useMemo(() => {
    const breakdown = configuration?.breakdown.find(
      (element) => element.key === 'totDue'
    );
    return {
      label: breakdown?.label || 'Due',
      infoText: breakdown?.infoText,
    };
  }, [configuration]);

  const penaltiesLabel = useMemo(() => {
    const breakdown = configuration?.breakdown.find(
      (element) => element.key === 'latePenalties'
    );
    return {
      label: breakdown?.label || 'Penalties',
      infoText: breakdown?.infoText,
    };
  }, [configuration]);

  const interestLabel = useMemo(() => {
    const breakdown = configuration?.breakdown.find(
      (element) => element.key === 'accruedInterest'
    );
    return {
      label: breakdown?.label || 'Interest',
      infoText: breakdown?.infoText,
    };
  }, [configuration]);

  const taxDiscountLabel = useMemo(() => {
    const breakdown = configuration?.breakdown.find(
      (element) => element.key === 'credit'
    );
    return {
      label: breakdown?.label || 'Tax Discount',
      infoText: breakdown?.infoText,
    };
  }, [configuration]);

  return (
    <>
      <tr style={{ ...rowStyle, fontWeight: 'bold', fontSize: '14px' }}>
        <th
          scope="row"
          style={{
            padding: '8px 0 4px 8px',
            fontWeight: 'inherit',
            textAlign: 'left',
          }}
        >
          <LabelWithInfoIcon
            label={`<b>${configuration?.label}</b>` || ''}
            infoText={configuration?.infoText}
          />
        </th>
        <td style={{ textAlign: 'right', padding: '8px 0 4px' }}>
          <Text>{convertNumberToCurrency(total)}</Text>
        </td>
      </tr>
      <tr style={rowStyle}>
        <th
          scope="row"
          style={{
            padding: '4px 0 4px 16px',
            fontWeight: 'normal',
            textAlign: 'left',
          }}
        >
          <LabelWithInfoIcon
            label={dueLabel.label}
            infoText={dueLabel.infoText}
          />
        </th>
        <td style={{ textAlign: 'right', padding: '4px 0' }}>
          <Text>{convertNumberToCurrency(totDue)}</Text>
        </td>
      </tr>
      {(!!credit || credit === 0) && (
        <tr style={rowStyle}>
          <th
            scope="row"
            style={{
              padding: '4px 0 4px 16px',
              fontWeight: 'normal',
              textAlign: 'left',
            }}
          >
            <LabelWithInfoIcon
              label={taxDiscountLabel.label}
              infoText={taxDiscountLabel.infoText}
            />
          </th>
          <td style={{ textAlign: 'right', padding: '4px 0' }}>
            <Text>{convertNumberToCurrency(credit)}</Text>
          </td>
        </tr>
      )}
      <tr style={rowStyle}>
        <th
          scope="row"
          style={{
            padding: '4px 0 4px 16px',
            fontWeight: 'normal',
            textAlign: 'left',
          }}
        >
          <LabelWithInfoIcon
            label={penaltiesLabel.label}
            infoText={penaltiesLabel.infoText}
          />
        </th>
        <td style={{ textAlign: 'right', padding: '4px 0' }}>
          <Text>{convertNumberToCurrency(latePenalties)}</Text>
        </td>
      </tr>
      <tr style={rowStyle}>
        <th
          scope="row"
          style={{
            padding: '4px 0 8px 16px',
            fontWeight: 'normal',
            textAlign: 'left',
          }}
        >
          <LabelWithInfoIcon
            label={interestLabel.label}
            infoText={interestLabel.infoText}
          />
        </th>
        <td style={{ textAlign: 'right', padding: '4px 0 8px' }}>
          <Text>{convertNumberToCurrency(accruedInterest)}</Text>
        </td>
      </tr>
    </>
  );
};
